/* eslint-disable func-names */
import React, { useEffect, useRef } from 'react';
import Head from 'next/head';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { AppProps } from 'next/app';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { Provider } from 'react-redux';
import { Router, useRouter } from 'next/router';
import { Toaster } from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { DefaultSeo } from 'next-seo';
import SEOConfig from '../next-seo.config';
import { ThemeSettings } from '../src/theme/Theme';
import SweetAlertTheme from '../src/theme/SweetAlertTheme';
import CaiThemeVars from '../src/components/ConnectAI/caiThemeVars';
import createEmotionCache from '../src/createEmotionCache';
import Store, { useSelector, AppState } from '../src/store/Store';
import Hotjar from '@hotjar/browser';
import RTL from '../src/layouts/full/shared/customizer/RTL';
import '../src/utils/i18n';
import './styles.css';
import {
  initializeGA4,
  shouldTrackAppPageView,
  trackPageView,
  trackUTMParams,
} from '../src/utils/analytics';
import { initializeClarity, syncClarityFunnelPage } from '../src/utils/clarity';
import { useScrollTracking } from '../src/hooks/useScrollTracking';
import { initOutboundBufferStore } from '../stores/outboundBufferStore';
import { initPushNotificationStore } from '../stores/pushNotificationStore';

// CSS FILES
import 'react-quill/dist/quill.snow.css';

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

if (!Sentry.getCurrentHub().getClient()) {
  Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    // Performance Monitoring disabled
    tracesSampleRate: 0,
    // Session Replay disabled
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    enabled: process.env.NEXT_PUBLIC_NODE_ENV === 'production',
  });
}

NProgress.configure({ showSpinner: false });
Router.events.on('routeChangeStart', () => NProgress.start());
Router.events.on('routeChangeComplete', () => NProgress.done());
Router.events.on('routeChangeError', () => NProgress.done());

function MyApp(props: MyAppProps) {
  const {
    Component,
    emotionCache = clientSideEmotionCache,
    pageProps,
  }: any = props;
  const theme = ThemeSettings();
  const customizer = useSelector((state: AppState) => state.customizer);
  const hotjarInitialized = useRef(false);
  const router = useRouter();
  
  const trackPageViews = shouldTrackAppPageView(router.pathname);
  const isMarketingPage = !router.pathname.startsWith('/bot');

  // Enable scroll tracking only for marketing pages
  useScrollTracking(isMarketingPage);

  useEffect(() => {
    initOutboundBufferStore();
  }, []);

  useEffect(() => {
    // Initialize GA4 centrally
    initializeGA4();
    initializeClarity();

    if (!hotjarInitialized.current) {
      Hotjar.init(5143179, 6, {
        debug: process.env.NEXT_PUBLIC_NODE_ENV === 'production',
      });
      hotjarInitialized.current = true;
    }
  }, []);

  useEffect(() => {
    syncClarityFunnelPage(router.pathname);

    if (trackPageViews) {
      trackPageView(router.pathname);
      trackUTMParams();
    }
  }, [router.pathname, trackPageViews]);

  useEffect(() => {
    initPushNotificationStore();
  }, []);

  return (
    <CacheProvider value={emotionCache}>
      <DefaultSeo {...SEOConfig} />
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
      </Head>
      <ThemeProvider theme={theme}>
        <RTL direction={customizer.activeDir}>
          <CssBaseline />
          <SweetAlertTheme />
          <CaiThemeVars />
          <Component {...pageProps} />
          <Toaster
            position="bottom-center"
          />
        </RTL>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default function (props: MyAppProps) {
  return (
    <Provider store={Store}>
      <MyApp {...props} />
    </Provider>
  );
}
